import React, { useRef, useEffect } from 'react';
import clsx from 'clsx';
import { makeStyles, useTheme } from '@mui/styles';
import PropTypes from 'prop-types';
import {
    select as D3select,
    scaleOrdinal as D3scaleOrdinal,
} from 'd3';
import D3Gauge from '../../d3/D3Gauge';

const useStyles = makeStyles(theme => ({
    root: {
        '& .circle': {
            stroke: theme.styled.slider.guage, // palette.grey[700],
            fill: theme.styled.slider.guage, // theme.palette.grey[700],
            color: theme.styled.slider.guage, // theme.palette.grey[700],
            strokeLinejoin: 'round',
            strokeLinecap: 'butt',
            strokeWidth: 7,
        },

        '& .band': {
            strokeWidth: 7,
            '&.value': {
                stroke: theme.palette.info.light,
                fill: theme.palette.info.light,
                color: theme.palette.info.light,
                strokeLinejoin: 'round',
                strokeLinecap: 'butt',
            }
        },

        '&.small .band': {
            strokeWidth: 4,
        },

        '&.small .circle': {
            strokeWidth: 4,
        },

        '& .valLabel': {
            stroke: theme.palette.info.light,
            fill: theme.palette.info.light,
            color: theme.palette.info.light,
        },

        '& .inner': {
            stroke: theme.palette.info.light,
            strokeWidth: 1,
            fill: theme.palette.info.light,
        },

        '& .pointer': {
            fill: theme.palette.info.light,
            stroke: theme.palette.info.light,
            strokeWidth: 1.5,
            fillOpacity: 0.7
        },

        '& .text': {
            fill: theme.palette.info.light,
        },

        '& .tick': {
            stroke: theme.palette.info.light,
            strokeWidth: 1,
            '&.major': {
                strokeWidth: 2,
            }
        },
    }
}));

const Gauge = (props) => {
    const d3Container = useRef(null);
    const classes = useStyles();
    const theme = useTheme();
    const severityColors = theme.SeverityColors;

    useEffect((didUpdate) => {
        if (d3Container.current) {
            const node = d3Container.current;

            // color coding for severity cutoffs or data series if passed
            let key = 'normal';
            let colors;
            Object.keys(props.severities || {}).forEach(s => {
                if (key === 'normal' && props.value < props.severities[s]) key = s;
            });

            if (key !== 'normal' || (props.data && props.data.length > 0)) {
                colors = D3scaleOrdinal()
                    .domain(['red', 'yellow', 'green', 'blue', 'unknown'])
                    .range(severityColors.concat('grey'));
            }

            // set attributes for object consistency so the gauge selection can survive updates
            const data = {
                id: 'once',
                series: props.data,
                value: props.value,
                label: props.label,
                key
            };

            const size = props.size || 120;

            D3select(node).selectAll('svg').data([data])
                .join('svg')
                .attr('width', size)
                .attr('height', props.small ? size : size * 0.8)
                .call(D3Gauge()
                    .theme(theme)
                    .single(props.data === undefined)
                    .colors(colors)
                    .size(size)
                    .small(props.small));
        }
    }, [props.size, d3Container.current, props.data, props.value]);

    return (
        <div className={clsx(props.className, classes.root, { small: props.small })} ref={d3Container}></div>
    );
};

Gauge.propTypes = {
    size: PropTypes.number,
    small: PropTypes.bool,
    severities: PropTypes.object,
    value: PropTypes.number.isRequired,
    label: PropTypes.string.isRequired,
    data: PropTypes.array,
    className: PropTypes.string,
};

export default Gauge;
